import {_state} from "../state/state.js";

/* Thousands-separator helpers for the range inputs (price / mileage). */
const groupDigits = (v) => {
    const d = String(v).replace(/\D/g, '');
    return d ? d.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '';
};
const rawDigits = (v) => String(v).replace(/[^\d.]/g, '');
// Write a numeric value into a range input, grouped with commas when it opts in.
const setRangeValue = (input, num) => {
    if (num === '' || num === null || num === undefined || isNaN(num)) {
        input.value = '';
        return;
    }
    input.value = input.hasAttribute('data-group') ? groupDigits(num) : String(Math.round(num));
};

export const busesArchive = () => {
    return new Promise((resolve) => {
        document.querySelectorAll('[data-buses-archive]').forEach((root) => init(root));
        resolve();
    });
};

const init = (root) => {
    const form = root.querySelector('[data-buses-form]');
    const grid = root.querySelector('[data-buses-grid]');
    const paginationWrap = root.querySelector('[data-buses-pagination]');
    const countEl = root.querySelector('[data-buses-count]');
    const sortEl = root.querySelector('[data-buses-sort]');
    const filterPanel = root.querySelector('[data-buses-filter]');
    const activeCountEl = root.querySelector('[data-buses-active-count]');
    if (!form || !grid) return;

    // State classes (is-loading / is-filter-open) must live on the .buses-archive
    // element the SCSS targets — which may be `root` itself or a nested <section>
    // (the markup wraps it in <main data-buses-archive><section class="buses-archive">).
    const stateEl = root.classList.contains('buses-archive')
        ? root
        : (root.querySelector('.buses-archive') || root);

    const nonce = root.dataset.nonce || '';
    // Custom page param — WP reserves `paged` and 404s the archive when it exceeds
    // the main query's page count, so state lives in `bpage`.
    let currentPage = parseInt(new URLSearchParams(window.location.search).get('bpage'), 10) || 1;
    let loading = false;

    /* ---------- URL state (persist filters/sort/paged across reloads) ---------- */
    const syncUrl = (paged) => {
        const params = new URLSearchParams();
        // Group checked terms per taxonomy into a flat, comma-joined param
        // (?location=finland,sweden) instead of the ugly tax[slug][] brackets.
        const taxValues = {};
        form.querySelectorAll('input[type="checkbox"]:checked').forEach((c) => {
            const m = c.name.match(/^tax\[([^\]]+)\]/);
            const key = m ? m[1] : c.name;
            (taxValues[key] = taxValues[key] || []).push(c.value);
        });
        Object.keys(taxValues).forEach((k) => params.set(k, taxValues[k].join(',')));
        form.querySelectorAll('[data-range-from], [data-range-to]').forEach((i) => {
            const v = rawDigits(i.value);
            if (v !== '') params.set(i.name, v);
        });
        if (sortEl && sortEl.value && sortEl.value !== 'price_asc') params.set('sort', sortEl.value);
        if (paged > 1) params.set('bpage', paged);
        // keep commas literal (slugs are safe) so the URL stays readable
        const qs = params.toString().replace(/%2C/gi, ',');
        history.replaceState(null, '', window.location.pathname + (qs ? '?' + qs : ''));
    };

    /* ---------- AJAX ---------- */
    const request = (paged) => {
        if (loading) return;
        loading = true;
        stateEl.classList.add('is-loading');
        currentPage = paged;
        syncUrl(paged);

        const data = new FormData(form);
        // strip thousands separators so the server gets clean numbers
        form.querySelectorAll('[data-range-from], [data-range-to]').forEach((i) => {
            data.set(i.name, rawDigits(i.value));
        });
        data.append('action', 'busland_filter_buses');
        data.append('nonce', nonce);
        data.append('sort', sortEl ? sortEl.value : 'price_asc');
        data.append('bpage', paged);

        fetch(window.scriptData.ajaxUrl, {method: 'POST', body: data, credentials: 'same-origin'})
            .then((r) => r.json())
            .then((res) => {
                if (!res || !res.success) return;
                const d = res.data;
                grid.innerHTML = d.cards || '';
                if (paginationWrap) paginationWrap.innerHTML = d.pagination || '';
                if (countEl) countEl.textContent = d.found;
                updateCounts(d.counts);
            })
            .finally(() => {
                loading = false;
                stateEl.classList.remove('is-loading');
            });
    };

    const updateCounts = (counts) => {
        if (!counts) return;
        Object.keys(counts).forEach((tax) => {
            Object.keys(counts[tax]).forEach((slug) => {
                const el = form.querySelector(`[data-count-for="${tax}|${slug}"]`);
                if (el) el.textContent = `(${counts[tax][slug]})`;
            });
        });
    };

    const updateActiveCount = () => {
        const checks = form.querySelectorAll('input[type="checkbox"]:checked').length;
        const ranges = [...form.querySelectorAll('[data-range-from], [data-range-to]')].filter((i) => i.value.trim() !== '').length;
        const n = checks + ranges;
        if (activeCountEl) {
            activeCountEl.textContent = `(${n})`;
            activeCountEl.classList.toggle('is-active', n > 0);
        }
    };

    // Scroll back to the top of the archive section. The header is position:absolute
    // (gone once scrolled), so only a small breathing offset is needed.
    const scrollToSection = () => {
        const y = root.getBoundingClientRect().top + window.pageYOffset - 24;
        if (_state.lenisInit) _state.lenisInit.scrollTo(y);
        else window.scrollTo({top: Math.max(0, y), behavior: 'smooth'});
    };

    /* ---------- Filter interactions ---------- */
    form.addEventListener('submit', (e) => {
        e.preventDefault();
        request(1);
        closeModal();
        scrollToSection();
    });

    if (sortEl) sortEl.addEventListener('change', () => request(1));
    initSortSelect(root, sortEl);

    // pagination (delegated — content is replaced)
    if (paginationWrap) {
        paginationWrap.addEventListener('click', (e) => {
            const btn = e.target.closest('[data-page]');
            if (!btn) return;
            request(parseInt(btn.dataset.page, 10) || 1);
            scrollToSection();
        });
    }

    // clear all
    const clearBtn = root.querySelector('[data-buses-clear]');
    if (clearBtn) {
        clearBtn.addEventListener('click', () => {
            form.querySelectorAll('input[type="checkbox"]').forEach((c) => (c.checked = false));
            form.querySelectorAll('[data-range-from], [data-range-to]').forEach((i) => (i.value = ''));
            root.querySelectorAll('[data-range-slider]').forEach(resetSlider);
            updateActiveCount();
            request(1);
            closeModal();
            scrollToSection();
        });
    }

    form.addEventListener('change', updateActiveCount);
    updateActiveCount();

    // accordion sections
    form.querySelectorAll('[data-filter-toggle]').forEach((head) => {
        head.addEventListener('click', () => {
            head.closest('[data-filter-section]').classList.toggle('is-collapsed');
        });
    });

    // thousands-separator formatting for large-magnitude ranges (price/mileage);
    // reformat on blur so the caret isn't disturbed mid-typing
    form.querySelectorAll('[data-group]').forEach((i) => {
        i.addEventListener('blur', () => { i.value = groupDigits(i.value); });
    });

    // price slider(s)
    root.querySelectorAll('[data-range-slider]').forEach((slider) => initSlider(slider));

    /* ---------- Mobile modal ---------- */
    const openBtn = root.querySelector('[data-buses-filter-open]');
    const closeBtn = root.querySelector('[data-buses-filter-close]');

    const lockScroll = (lock) => {
        document.documentElement.classList.toggle('overflow-hidden', lock);
        document.body.classList.toggle('overflow-hidden', lock);
        if (_state.lenisInit) lock ? _state.lenisInit.stop() : _state.lenisInit.start();
    };
    const openModal = () => {
        stateEl.classList.add('is-filter-open');
        lockScroll(true);
    };
    function closeModal() {
        if (!stateEl.classList.contains('is-filter-open')) return;
        stateEl.classList.remove('is-filter-open');
        lockScroll(false);
    }

    if (openBtn) openBtn.addEventListener('click', openModal);
    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') closeModal();
    });
    // click on the dimmed backdrop (the panel's ::before area) closes it
    if (filterPanel) {
        root.addEventListener('click', (e) => {
            if (stateEl.classList.contains('is-filter-open') && e.target === root) closeModal();
        });
    }
};

/* ---------- Dual-range slider ---------- */
const resetSlider = (slider) => {
    const min = parseFloat(slider.dataset.min) || 0;
    const max = parseFloat(slider.dataset.max) || 0;
    const lo = slider.querySelector('[data-slider-min]');
    const hi = slider.querySelector('[data-slider-max]');
    if (lo) lo.value = min;
    if (hi) hi.value = max;
    slider.dispatchEvent(new Event('slider-refresh'));
};

const initSlider = (slider) => {
    const min = parseFloat(slider.dataset.min) || 0;
    const max = parseFloat(slider.dataset.max) || 0;
    const lo = slider.querySelector('[data-slider-min]');
    const hi = slider.querySelector('[data-slider-max]');
    const fill = slider.querySelector('[data-slider-range]');
    const section = slider.closest('[data-range]');
    const fromInput = section ? section.querySelector('[data-range-from]') : null;
    const toInput = section ? section.querySelector('[data-range-to]') : null;
    if (!lo || !hi || max <= min) return;

    const pct = (v) => ((v - min) / (max - min)) * 100;

    const paint = () => {
        let a = parseFloat(lo.value);
        let b = parseFloat(hi.value);
        if (a > b - 1) { // keep handles from crossing
            if (document.activeElement === lo) a = b - 1;
            else b = a + 1;
            lo.value = a;
            hi.value = b;
        }
        if (fill) {
            fill.style.left = pct(a) + '%';
            fill.style.right = (100 - pct(b)) + '%';
        }
    };

    // slider -> number inputs (grouped display via setRangeValue)
    lo.addEventListener('input', () => {
        paint();
        if (fromInput) {
            const v = Math.round(parseFloat(lo.value));
            setRangeValue(fromInput, v > min ? v : '');
        }
    });
    hi.addEventListener('input', () => {
        paint();
        if (toInput) {
            const v = Math.round(parseFloat(hi.value));
            setRangeValue(toInput, v < max ? v : '');
        }
    });

    // number inputs -> slider (strip separators before reading)
    if (fromInput) fromInput.addEventListener('input', () => {
        const raw = rawDigits(fromInput.value);
        const v = raw === '' ? min : Math.max(min, Math.min(parseFloat(raw), parseFloat(hi.value) - 1));
        lo.value = v;
        paint();
    });
    if (toInput) toInput.addEventListener('input', () => {
        const raw = rawDigits(toInput.value);
        const v = raw === '' ? max : Math.min(max, Math.max(parseFloat(raw), parseFloat(lo.value) + 1));
        hi.value = v;
        paint();
    });

    slider.addEventListener('slider-refresh', paint);
    paint();
};

/**
 * Custom sort dropdown: a design-system `.custom-select` driving the hidden native
 * `<select data-buses-sort>` (kept as the value carrier — the archive's AJAX still
 * reads `sortEl.value` and listens to its `change`). Accessible: Tab focuses the
 * trigger, Enter/Space/Arrows operate the list, Esc closes.
 */
const initSortSelect = (root, real) => {
    const field = root.querySelector('[data-buses-sort-ui]');
    const custom = field && field.querySelector('.custom-select');
    if (!custom || !real) return;

    const trigger = custom.querySelector('.custom-select__trigger');
    const triggerText = trigger && trigger.querySelector('span');
    const options = [...custom.querySelectorAll('.custom-select__option')];
    if (!trigger || !options.length) return;

    // Native <select> is only a value carrier now — out of the tab order + a11y tree.
    real.setAttribute('tabindex', '-1');
    real.setAttribute('aria-hidden', 'true');
    trigger.setAttribute('role', 'button');
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    const listbox = custom.querySelector('.custom-select__dropdown');
    if (listbox) listbox.setAttribute('role', 'listbox');
    options.forEach((o) => o.setAttribute('role', 'option'));

    let suppressOpen = false;
    const open = () => { custom.classList.add('open'); trigger.setAttribute('aria-expanded', 'true'); };
    const close = () => { custom.classList.remove('open'); trigger.setAttribute('aria-expanded', 'false'); };

    const syncLabel = (value) => {
        options.forEach((o) => {
            const on = o.dataset.value === value;
            o.classList.toggle('is-selected', on);
            if (on && triggerText) triggerText.textContent = o.textContent.trim();
        });
    };

    const choose = (value) => {
        if (real.value !== value) {
            real.value = value;
            real.dispatchEvent(new Event('change', { bubbles: true })); // → archive request(1)
        }
        syncLabel(value);
        suppressOpen = true;
        trigger.focus();
        suppressOpen = false;
        close();
    };

    let wasOpen = false;
    trigger.addEventListener('mousedown', (e) => {
        // Don't let the mouse focus the trigger: otherwise a focusin fires right after
        // a click-to-close and re-opens the list (the "flicker" on clicking while open).
        e.preventDefault();
        wasOpen = custom.classList.contains('open');
    });
    trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        if (wasOpen) {
            close();
        } else {
            open();
            trigger.focus(); // keep keyboard nav available after a mouse-open
        }
    });
    trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
            e.preventDefault();
            open();
            options[0] && options[0].focus();
        } else if (e.key === 'Escape') {
            close();
        }
    });

    custom.addEventListener('focusin', () => { if (!suppressOpen) open(); });
    custom.addEventListener('focusout', (e) => { if (!custom.contains(e.relatedTarget)) close(); });

    options.forEach((option, i) => {
        option.tabIndex = 0;
        option.addEventListener('click', () => choose(option.dataset.value));
        option.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                choose(option.dataset.value);
            } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                (options[i + 1] || options[0]).focus();
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                (options[i - 1] || options[options.length - 1]).focus();
            } else if (e.key === 'Escape') {
                suppressOpen = true;
                trigger.focus();
                suppressOpen = false;
                close();
            }
        });
    });

    document.addEventListener('click', (e) => { if (!custom.contains(e.target)) close(); });
    // Keep the label in sync if the sort is changed programmatically elsewhere.
    real.addEventListener('change', () => syncLabel(real.value));
};
